<script setup lang="ts">
/**
 * The tidal stream atlases. Thirteen pictures per area, one per hour from HW−6 to HW+6 at
 * the area's reference harbour — which is what makes them useless on their own: the plates
 * carry no clock. The app supplies the clock, by finding that harbour's high waters in the
 * predictions and showing which plate stands for this moment, and at what time.
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { atlasPlate, type Extremum } from '@quicktide/shared'
import { useAppStore } from '../store'
import ScreenTitle from '../components/ScreenTitle.vue'
import InfoTip from '../components/InfoTip.vue'
import { extremaBetween, startOfLocalDay, addDays } from '../lib/tide'
import { ATLASES, atlasById, atlasForStation } from '../lib/atlases'
import { hold, holding, plateImage } from '../lib/plates'
import { springPhrase } from '../lib/springs'

const store = useAppStore()
const DAY = 86_400_000
const HOUR = 3_600_000

const atlas = computed(() =>
  atlasById(store.settings.atlasId) ?? atlasForStation(store.settings.stationId) ?? ATLASES[0]!)
/**
 * The picker shows the atlas actually on screen, including the one defaulted to.
 *
 * Choosing one here — and only here — fetches the whole of it onto the device, so the area
 * a skipper has said they are sailing in is on board whether or not there is a signal at
 * sea. Merely opening this screen downloads nothing: the atlas it opens on is a guess from
 * the chosen tide station, and nobody's data plan should pay for a guess.
 */
const chosen = computed({
  get: () => atlas.value.id,
  set: (id: string) => {
    store.settings.atlasId = id
    // a device without a registration may have the one plate, so there is no atlas to hold
    if (store.registered) void hold(id)
  }
})
const stationName = computed(() => store.stations.find(s => s.id === atlas.value.station)?.name ?? '')

/** The moment on show: now while the day is today, else the middle of the chosen day */
const now = ref(Date.now())
const offset = ref<number | null>(null)
let timer: number | undefined
onMounted(() => { timer = window.setInterval(() => { now.value = Date.now() }, 60_000) })
onUnmounted(() => window.clearInterval(timer))

const isToday = computed(() => store.selectedDay === startOfLocalDay(now.value))
const time = computed(() => {
  const base = isToday.value ? now.value : store.selectedDay + 12 * HOUR
  return base + (offset.value ?? 0)
})
// a different day starts again at its own beginning rather than keeping an hour count
watch(() => store.selectedDay, () => { offset.value = null })

const extrema = ref<Extremum[]>([])
watch([() => atlas.value.station, () => store.selectedDay, () => store.dataVersion], async () => {
  extrema.value = await extremaBetween(atlas.value.station, store.selectedDay - DAY, store.selectedDay + 2 * DAY)
}, { immediate: true })

/**
 * Without a registration the atlas is one plate, the one for high water, and it stands for
 * no particular time: enough to see what the atlas is, not to sail by it. The server keeps
 * the rest back, so this only spares the reader buttons that could not do anything.
 */
const demo = computed(() => !store.registered)
const plate = computed(() => (demo.value ? { hour: 0, time: null } : atlasPlate(extrema.value, time.value)))

/**
 * The picture, made from the bytes the API sent: an <img> cannot send the device's token.
 * Each new plate replaces the last, and the old picture's memory is given back.
 */
const src = ref('')
const denied = ref(false)
watch([() => atlas.value.id, () => plate.value?.hour], async ([id, hour], _old, onCleanup) => {
  let current = true
  onCleanup(() => { current = false })
  denied.value = false
  if (hour === undefined) { show(''); return }
  const image = await plateImage(id, hour)
  if (!current) return
  if (image === 'denied') { denied.value = true; show(''); failed.value = true; return }
  if (!image) { show(''); failed.value = true; return }
  show(URL.createObjectURL(image))
}, { immediate: true })
function show(url: string) {
  if (src.value) URL.revokeObjectURL(src.value)
  src.value = url
}
onUnmounted(() => show(''))

/**
 * The plate on screen is either this one or none: showing the previous area's chart while
 * the new one loads is worse than showing nothing, because it is a picture of the wrong
 * water. So the picture goes the moment the area changes, and what stands in its place
 * says why — fetching, or no signal to fetch with.
 */
const ready = ref(false)
const failed = ref(false)
watch([() => atlas.value.id, () => plate.value?.hour], () => { ready.value = false; failed.value = false })

/** "3 uur na HW Harlingen", the caption the old app printed under the plate */
const caption = computed(() => {
  const p = plate.value
  if (!p) return store.t('noData')
  // "voor HW" and "na HW" already carry the HW, so only the plate at high water adds it
  const n = Math.abs(p.hour)
  if (p.hour === 0) return `${store.t('high')} ${stationName.value}`
  const hours = `${n} ${n === 1 ? store.t('hour') : store.t('hours')}`
  return `${hours} ${p.hour < 0 ? store.t('beforeHW') : store.t('afterHW')} ${stationName.value}`
})
const plateClock = computed(() =>
  plate.value?.time ? new Date(plate.value.time).toLocaleTimeString(store.settings.locale, { hour: '2-digit', minute: '2-digit' }) : '')

function step(hours: number) {
  offset.value = (offset.value ?? 0) + hours * HOUR
}
function stepDay(days: number) {
  store.selectedDay = store.clampDay(addDays(store.selectedDay, days))
}
function backToNow() {
  offset.value = null
  store.selectedDay = store.clampDay(startOfLocalDay(Date.now()))
}

/**
 * Which of the two printed rates applies. The plates give a rate at neaps and one at
 * springs, so where the day sits between them is half of reading them.
 */
const springs = computed(() =>
  springPhrase(time.value, store.stations.find(s => s.id === atlas.value.station)?.springLagDays, store.t, store.tv))

/** The day, short enough to sit on the end of the caption rather than take a line of its own */
const dayLabel = computed(() => {
  if (isToday.value) return store.t('today')
  return new Date(store.selectedDay).toLocaleDateString(store.settings.locale, { weekday: 'short', day: 'numeric', month: 'short' })
})

// the next plate should be there before it is asked for: stepping an hour is the whole interaction
watch(src, () => {
  const p = plate.value
  if (!p || demo.value) return
  for (const h of [p.hour - 1, p.hour + 1]) {
    if (h >= -6 && h <= 6) void plateImage(atlas.value.id, h)
  }
})

/**
 * Pan and zoom.
 *
 * A stream plate is read by looking closely, so it drags with one finger and pinches with
 * two, the way any chart does. Both keep the point under the fingers where it is — zooming
 * about the centre instead would slide whatever you were looking at off the screen. The
 * buttons stay for anyone not holding the tablet.
 */
const zoom = ref(1)
const pan = ref({ x: 0, y: 0 })
const dragging = ref(false)
const MAX_ZOOM = 8

/** Where the fingers are, so a second one can be noticed the moment it lands */
const touches = new Map<number, { x: number; y: number }>()
let gesture: { x: number; y: number; span: number; pan: { x: number; y: number }; zoom: number } | null = null

function clampPan() {
  // never let the plate be dragged entirely off the screen
  const limit = 0.5 * (zoom.value - 1) * 100
  pan.value.x = Math.max(-limit, Math.min(limit, pan.value.x))
  pan.value.y = Math.max(-limit, Math.min(limit, pan.value.y))
}

/**
 * Zoom while holding one point still. In the element's own units, a content point sits at
 * `screen = content * zoom + pan`; keeping it put across a change of zoom fixes the pan.
 */
function zoomAbout(next: number, at: { x: number; y: number }) {
  const from = zoom.value
  zoom.value = Math.max(1, Math.min(MAX_ZOOM, next))
  if (zoom.value === 1) { pan.value = { x: 0, y: 0 }; return }
  const factor = zoom.value / from
  pan.value = {
    x: at.x - (at.x - pan.value.x) * factor,
    y: at.y - (at.y - pan.value.y) * factor
  }
  clampPan()
}

function setZoom(z: number) {
  zoomAbout(z, { x: 0, y: 0 })
}

/** A point in the plate's box, as a percentage of its size measured from the centre */
function within(e: PointerEvent | { clientX: number; clientY: number }, box: DOMRect) {
  return {
    x: ((e.clientX - box.left) / box.width - 0.5) * 100,
    y: ((e.clientY - box.top) / box.height - 0.5) * 100
  }
}

function begin(element: Element) {
  const points = [...touches.values()]
  const box = element.getBoundingClientRect()
  const middle = {
    clientX: points.reduce((n, p) => n + p.x, 0) / points.length,
    clientY: points.reduce((n, p) => n + p.y, 0) / points.length
  }
  const at = within(middle, box)
  const span = points.length > 1 ? Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y) : 0
  gesture = { ...at, span, pan: { ...pan.value }, zoom: zoom.value }
}

function onDown(e: PointerEvent) {
  const element = e.currentTarget as Element
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY })
  element.setPointerCapture(e.pointerId)
  dragging.value = true
  begin(element)
}

function onMove(e: PointerEvent) {
  if (!touches.has(e.pointerId) || !gesture) return
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY })
  const element = e.currentTarget as Element
  const box = element.getBoundingClientRect()
  const points = [...touches.values()]

  if (points.length > 1) {
    // two fingers: the distance between them sets the zoom, their middle sets the pan
    const span = Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y)
    const middle = within({
      clientX: (points[0]!.x + points[1]!.x) / 2,
      clientY: (points[0]!.y + points[1]!.y) / 2
    }, box)
    const wanted = Math.max(1, Math.min(MAX_ZOOM, gesture.zoom * (span / (gesture.span || span))))
    const factor = wanted / gesture.zoom
    zoom.value = wanted
    pan.value = {
      x: middle.x - (gesture.x - gesture.pan.x) * factor,
      y: middle.y - (gesture.y - gesture.pan.y) * factor
    }
    if (zoom.value === 1) pan.value = { x: 0, y: 0 }
    else clampPan()
    return
  }

  if (zoom.value === 1) return                 // nothing to drag while the plate fits
  const here = within(e, box)
  pan.value = { x: gesture.pan.x + (here.x - gesture.x), y: gesture.pan.y + (here.y - gesture.y) }
  clampPan()
}

function onUp(e: PointerEvent) {
  touches.delete(e.pointerId)
  ;(e.currentTarget as Element).releasePointerCapture?.(e.pointerId)
  if (!touches.size) { dragging.value = false; gesture = null; return }
  begin(e.currentTarget as Element)            // a finger lifted: carry on with what is left
}

function onWheel(e: WheelEvent) {
  e.preventDefault()
  const box = (e.currentTarget as Element).getBoundingClientRect()
  zoomAbout(zoom.value * (e.deltaY < 0 ? 1.15 : 1 / 1.15), within(e, box))
}

/** A double tap is the quickest way back to the whole sheet */
function onDouble() {
  setZoom(zoom.value > 1 ? 1 : 2.5)
}

watch(atlas, () => { setZoom(1); pan.value = { x: 0, y: 0 } })
</script>

<template>
  <main class="screen stroomscreen">
    <div class="header">
      <ScreenTitle>{{ store.t('currents') }}</ScreenTitle>
      <select v-model="chosen" class="atlaspick" :aria-label="store.t('currents')">
        <option v-for="a in ATLASES" :key="a.id" :value="a.id">{{ a.name[store.settings.locale] }}</option>
      </select>
    </div>

    <p class="platecaption">
      <span class="level">{{ caption }}</span>
      <span v-if="plateClock" class="unit">· {{ plateClock }} · {{ dayLabel }} · {{ springs }}</span>
      <span v-if="holding" class="unit small">· {{ store.t('holdingPlates') }} {{ holding.done }}/{{ holding.total }}</span>
      <InfoTip :title="store.t('currents')" :texts="[store.t('atlasNote')]" />
    </p>

    <div class="plate" :class="{ zoomed: zoom > 1, dragging }"
         @pointerdown="onDown" @pointermove="onMove" @pointerup="onUp" @pointercancel="onUp"
         @wheel="onWheel" @dblclick="onDouble">
      <img v-if="src" :key="src" v-show="ready" :src="src" :alt="caption"
           :style="{ transform: `translate(${pan.x}%, ${pan.y}%) scale(${zoom})` }"
           @load="ready = true" @error="failed = true">
      <div v-if="!ready" class="plateloading">
        <template v-if="failed && !store.online">
          <p class="strong">{{ store.t('offlinePlates') }}</p>
          <p class="small muted">{{ atlas.name[store.settings.locale] }}</p>
        </template>
        <template v-else-if="failed">
          <p class="strong">{{ store.t('platesMissing') }}</p>
        </template>
        <template v-else-if="holding">
          <p>{{ store.t('holdingPlates') }}</p>
          <p class="num">{{ holding.done }} / {{ holding.total }}</p>
          <div class="platebar"><span :style="{ width: `${(holding.done / holding.total) * 100}%` }" /></div>
        </template>
        <template v-else>
          <p class="muted">{{ store.t('loadingPlate') }}</p>
        </template>
      </div>
      <div class="mapzoom">
        <button :aria-label="store.t('zoomIn')" @click="setZoom(zoom * 1.5)">+</button>
        <button :aria-label="store.t('zoomOut')" @click="setZoom(zoom / 1.5)">−</button>
      </div>
    </div>

    <!-- one row from a day back to a day on, so the picture keeps the rest of the screen -->
    <div class="streamstrip">
      <button :disabled="demo" @click="stepDay(-1)">{{ store.t('minusDay') }}</button>
      <button :disabled="demo" @click="step(-1)">−1 {{ store.t('hour') }}</button>
      <button class="primary" :disabled="demo" @click="backToNow()">{{ store.t('now') }}</button>
      <button :disabled="demo" @click="step(1)">+1 {{ store.t('hour') }}</button>
      <button :disabled="demo" @click="stepDay(1)">{{ store.t('plusDay') }}</button>
    </div>
    <p v-if="demo" class="notice small">{{ store.t('platesDemo') }}</p>

  </main>
</template>
